import { useEffect, useState } from "react";
import { api, errMsg, formatDate, uploadImage, downloadFile } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { useAuth } from "@/context/AuthContext";
import { Camera, Download, Loader2, LogIn, LogOut, MapPin } from "lucide-react";
import { PhotoViewer } from "@/components/SpkPanel";

const todayWib = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
const timeWib = (iso) => iso ? new Date(iso).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }) : "-";

function getLocation() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve({}), { enableHighAccuracy: true, timeout: 10000 });
  });
}

const MapLink = ({ p }) => p?.lat ? (
  <a href={`https://maps.google.com/?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#B87333] text-xs">
    <MapPin size={12} />Lokasi
  </a>
) : <span className="text-xs text-[#8A999A]">Tanpa lokasi</span>;

function CheckCard({ today, onDone }) {
  const [type, setType] = useState("Office");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isOut = today && !today.check_out;
  const finished = today?.check_out;

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data.filter(p => p.status !== "Selesai"))); }, []);

  async function onPhoto(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!isOut && type === "Lapangan" && !projectId) { setErr("Pilih project dulu"); return; }
    setBusy(true); setErr("");
    try {
      const [photo, loc] = await Promise.all([uploadImage(file), getLocation()]);
      if (isOut) await api.post("/attendance/check-out", { photo, ...loc });
      else await api.post("/attendance/check-in", { photo, type, project_id: type === "Lapangan" ? projectId : null, ...loc });
      onDone();
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  }

  return (
    <div className="card-editorial p-5 space-y-4" data-testid="attendance-check-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#B87333]">Hari ini</div>
          <div className="font-serif-display text-xl font-bold">{formatDate(new Date().toISOString())}</div>
        </div>
        {today && <span className={`chip ${today.status === "Terlambat" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="attendance-today-status">{today.status}</span>}
      </div>

      {today && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="p-3 bg-[#FAF7F2] rounded-lg"><div className="text-[10px] uppercase text-[#B87333]">Masuk</div><div className="font-mono-num text-lg font-semibold" data-testid="attendance-in-time">{timeWib(today.check_in?.time)}</div><div className="text-xs text-[#8A999A]">{today.type}{today.project_name ? ` · ${today.project_name}` : ""}</div></div>
          <div className="p-3 bg-[#FAF7F2] rounded-lg"><div className="text-[10px] uppercase text-[#B87333]">Pulang</div><div className="font-mono-num text-lg font-semibold" data-testid="attendance-out-time">{timeWib(today.check_out?.time)}</div>{today.work_hours != null && <div className="text-xs text-[#8A999A]">{today.work_hours} jam</div>}</div>
        </div>
      )}

      {!today && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {["Office", "Lapangan"].map(t => (
              <button key={t} onClick={() => setType(t)} data-testid={`attendance-type-${t.toLowerCase()}`}
                className={`h-11 rounded-lg font-medium ${type === t ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>{t === "Office" ? "Office" : "Lapangan"}</button>
            ))}
          </div>
          {type === "Lapangan" && <p className="text-[11px] text-[#536263]">Lokasi GPS dicek terhadap venue project. Absen ditolak bila di luar radius.</p>}
          {type === "Lapangan" && (
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" data-testid="attendance-project-select">
              <option value="">Pilih project / venue</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
        </>
      )}

      {!finished && (
        <label className={`btn-primary w-full cursor-pointer h-14 text-base ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={isOut ? "attendance-checkout-btn" : "attendance-checkin-btn"}>
          {busy ? <Loader2 className="animate-spin" size={20} /> : isOut ? <LogOut size={20} /> : <LogIn size={20} />}
          {isOut ? "Check-out (Selfie)" : "Check-in (Selfie)"}
          <Camera size={18} className="opacity-70" />
          <input type="file" accept="image/*" capture="user" onChange={onPhoto} className="hidden" data-testid="attendance-photo-input" />
        </label>
      )}
      {finished && <p className="text-sm text-center text-emerald-700">Absen hari ini lengkap. Terima kasih!</p>}
      {err && <div className="text-sm text-red-700" data-testid="attendance-error">{err}</div>}
      <p className="text-[11px] text-[#8A999A]">Foto selfie &amp; lokasi GPS disimpan sebagai bukti kehadiran.</p>
    </div>
  );
}

function Row({ a, onPhoto, showName }) {
  return (
    <div className="card-editorial p-3 flex items-center gap-3" data-testid={`attendance-row-${a.id}`}>
      <button onClick={() => onPhoto(a.check_in?.photo)} className="shrink-0">
        <AuthImage path={a.check_in?.photo} alt="Selfie" className="w-12 h-12 rounded-lg object-cover border border-[#E2DDD3]" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{showName ? a.user_name : formatDate(a.date)}</div>
        <div className="text-xs text-[#8A999A] truncate">{showName ? `${a.role} · ` : ""}{a.type}{a.project_name ? ` · ${a.project_name}` : ""}</div>
        <div className="flex items-center gap-2"><MapLink p={a.check_in} />{a.distance_m != null && <span className="text-[11px] text-[#2D6A4F]" data-testid={`attendance-distance-${a.id}`}>{a.distance_m} m dari venue</span>}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono-num text-sm">{timeWib(a.check_in?.time)} – {timeWib(a.check_out?.time)}</div>
        <span className={`chip mt-1 ${a.status === "Terlambat" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>{a.status}</span>
      </div>
    </div>
  );
}

function TeamView({ onPhoto }) {
  const [date, setDate] = useState(todayWib());
  const [month, setMonth] = useState(todayWib().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [recap, setRecap] = useState([]);
  useEffect(() => { api.get("/attendance", { params: { date } }).then(r => setRows(r.data)); }, [date]);
  useEffect(() => { api.get("/attendance/recap", { params: { month } }).then(r => setRecap(r.data)); }, [month]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif-display text-xl font-semibold">Absen Tim</h2>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border border-[#E2DDD3] rounded-lg px-3 h-10" data-testid="attendance-date-filter" />
        </div>
        <div className="space-y-2" data-testid="attendance-team-list">
          {rows.map(a => <Row key={a.id} a={a} onPhoto={onPhoto} showName />)}
          {rows.length === 0 && <p className="text-sm text-[#8A999A]">Belum ada yang absen</p>}
        </div>
      </div>
      <div className="card-editorial p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-serif-display text-xl font-semibold">Rekap Bulanan</h2>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="border border-[#E2DDD3] rounded-lg px-3 h-10" data-testid="attendance-month-filter" />
        </div>
        <div className="flex gap-2 mb-3 flex-wrap">
          <button onClick={() => downloadFile("/attendance/recap/export", `rekap-absensi-${month}.xlsx`, { month, format: "xlsx" })} className="btn-primary" data-testid="attendance-export-xlsx"><Download size={16} />Excel</button>
          <button onClick={() => downloadFile("/attendance/recap/export", `rekap-absensi-${month}.csv`, { month, format: "csv" })} className="btn-copper" data-testid="attendance-export-csv"><Download size={16} />CSV</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="attendance-recap-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#B87333]">
              <th className="py-2">Nama</th><th>Hadir</th><th>Office</th><th>Lapangan</th><th>Terlambat</th><th>Tanpa Pulang</th><th>Jam</th>
            </tr></thead>
            <tbody>
              {recap.map(r => (
                <tr key={r.user_id} className="border-t border-[#E2DDD3]">
                  <td className="py-2"><div className="font-medium">{r.name}</div><div className="text-xs text-[#8A999A]">{r.role}</div></td>
                  <td className="font-mono-num">{r.days_present}</td><td className="font-mono-num">{r.office}</td><td className="font-mono-num">{r.field}</td>
                  <td className={`font-mono-num ${r.late ? "text-red-700" : ""}`}>{r.late}</td><td className="font-mono-num">{r.no_checkout}</td><td className="font-mono-num">{r.total_hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [viewing, setViewing] = useState(null);
  const isManager = ["Owner", "Finance Admin"].includes(user?.role);

  async function load() {
    const [t, h] = await Promise.all([api.get("/attendance/today"), api.get("/attendance", { params: { month: todayWib().slice(0, 7), user_id: user.id } })]);
    setToday(t.data); setHistory(h.data);
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 sm:p-8 space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Absensi</h1>
        <p className="text-sm text-[#536263]">Check-in &amp; check-out tim office dan lapangan</p>
        <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
      </header>
      <div className="grid lg:grid-cols-2 gap-6">
        <CheckCard today={today} onDone={load} />
        <div className="space-y-2">
          <h2 className="font-serif-display text-xl font-semibold">Riwayat Saya Bulan Ini</h2>
          <div className="space-y-2" data-testid="attendance-history">
            {history.map(a => <Row key={a.id} a={a} onPhoto={setViewing} />)}
            {history.length === 0 && <p className="text-sm text-[#8A999A]">Belum ada riwayat</p>}
          </div>
        </div>
      </div>
      {isManager && <TeamView onPhoto={setViewing} />}
      <PhotoViewer path={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
